import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RestSerialService } from 'src/app/service/rest/serial/rest-serial.service';
import { TranslateFirebaseService } from 'src/app/service/utils/translate-firebase.service';
import { SerialNumber, SerialNumberUpdate, SerialNumbersImportStatus, ImportProgress } from '../device/device.model';
import { Tag } from '../utils.model';

@Injectable()
export class SerialNumberService {
  
  private _serialNumber : SerialNumber = new SerialNumber();
  private _serialNumberUpdate: SerialNumberUpdate = new SerialNumberUpdate();
  private _importStatus: SerialNumbersImportStatus = new SerialNumbersImportStatus();
  private _currentImportProgress: ImportProgress | null = null;
  private _importEventSource: EventSource | null = null;
  
  // Long polling implementation for import status
  private _pollingActive: boolean = false;
  private _pollingInterval: any = null;
  private _pollingDelay: number = 500; // 500ms between requests for faster updates
  private _pollingSuspended: boolean = false; // Flag to temporarily suspend polling during control operations
  private _suspensionTimeout: any = null;
  
  constructor(private serialRest : RestSerialService,
    private translate : TranslateFirebaseService) { }
    
  get serialNumber() : SerialNumber {
    return this._serialNumber;
  }
    
  set serialNumber(serial : SerialNumber) {
    this._serialNumber = serial;
  }
  
  public get serialNumberUpdate(): SerialNumberUpdate {
    return this._serialNumberUpdate;
  }
  
  public set serialNumberUpdate(value: SerialNumberUpdate) {
    this._serialNumberUpdate = value;
  }
  
  get deviceType() : Tag[]{
    return JSON.parse(this.translate.getTranslation('enum_device_type'));
  }

  public get importStatus(): SerialNumbersImportStatus {
    return this._importStatus;
  }
  
  public get currentImportProgress(): ImportProgress | null {
    return this._currentImportProgress;
  }
  
  public set currentImportProgress(value: ImportProgress | null) {
    this._currentImportProgress = value;
  }

  // Serial Number Retrieval Methods
  getLastImportStatus(): Observable<SerialNumbersImportStatus> {
    return this.serialRest.getLastImportStatus();
  }
  
  createSerial(): Observable<SerialNumber> {
    return this.serialRest.createSerial(this._serialNumber);
  }
  
  updateSerial(): Observable<SerialNumber> {
    return this.serialRest.updateSerial(this._serialNumber.id, this._serialNumberUpdate);
  }
  
  deleteSerial(): Observable<any> {
    return this.serialRest.deleteSerial(this._serialNumber.id);
  }

  // Import Monitoring Methods
  private _lastPollTime: Date | null = null;
  
  startImportStatusPolling(onMessage: (progress: ImportProgress) => void, onError?: (error: any) => void): void {
    this.stopImportStatusPolling();
    this._pollingActive = true;
    const poll = () => {
      if (!this._pollingActive) return;
      
      // Skip polling if suspended due to control operation
      if (this._pollingSuspended) {
        this._pollingInterval = setTimeout(poll, this._pollingDelay);
        return;
      }
      
      this.serialRest.getImportStatusPolling(this._lastPollTime).subscribe({
        next: (dataArray) => {
          if (dataArray && Array.isArray(dataArray) && dataArray.length > 0) {
            // Aggiorniamo l'ultima volta che abbiamo fatto polling
            this._lastPollTime = new Date();
            
            try {
              // Utilizziamo l'ultimo elemento dell'array che dovrebbe essere il più recente
              const data = dataArray[dataArray.length - 1];
              
              const normalized: ImportProgress = {
                currentSerialNumber: data.currentSerialNumber || data.currentSerial || data.serial || '',
                imported: Number(data.imported || 0),
                error: Number(data.error || 0),
                skipped: Number(data.skipped || 0),
                remaining: Number(data.remaining || 0),
                success: data.success === true || data.success === 'true'
              };
              if (data.totale !== undefined || data.total !== undefined) 
                (normalized as any).totale = Number(data.totale || data.total || 0);
              if (data.totaleErrori !== undefined || data.totalErrors !== undefined)
                (normalized as any).totaleErrori = Number(data.totaleErrori || data.totalErrors || 0);
              if (data.totaleSkipped !== undefined || data.totalSkipped !== undefined)
                (normalized as any).totaleSkipped = Number(data.totaleSkipped || data.totalSkipped || 0);
              this._currentImportProgress = normalized;
              onMessage(normalized);
            } catch (err) {
              if (onError) onError({ type: 'normalization_error', error: err, data: dataArray });
            }
          }
          // else: risposta vuota o array vuoto, nessun update
        },
        error: (err) => {
          if (onError) onError({ type: 'polling_error', error: err });
        },
        complete: () => {
          if (this._pollingActive) {
            this._pollingInterval = setTimeout(poll, this._pollingDelay);
          }
        }
      });
    };
    poll();
  }

  stopImportStatusPolling(): void {
    this._pollingActive = false;
    if (this._pollingInterval) {
      clearTimeout(this._pollingInterval);
      this._pollingInterval = null;
    }
    this.resumePolling(); // Clear any suspension when stopping
  }

  // Temporarily suspend polling during control operations
  suspendPolling(durationMs: number = 3000): void {
    this._pollingSuspended = true;
    
    // Clear any existing suspension timeout
    if (this._suspensionTimeout) {
      clearTimeout(this._suspensionTimeout);
    }
    
    // Auto-resume after specified duration
    this._suspensionTimeout = setTimeout(() => {
      this.resumePolling();
    }, durationMs);
  }

  // Resume polling after control operation
  resumePolling(): void {
    this._pollingSuspended = false;
    if (this._suspensionTimeout) {
      clearTimeout(this._suspensionTimeout);
      this._suspensionTimeout = null;
    }
  }
  
  resetLastPollTime(): void {
    this._lastPollTime = null;
  }

  // Import Control Methods
  uploadCsvForImport(file: File): Observable<any> {
    return this.serialRest.uploadCsvForImport(file);
  }
  
  pauseImport(): Observable<any> {
    // Suspend polling during pause operation to give it priority
    this.suspendPolling(3000);
    return this.serialRest.pauseImport();
  }
  
  resumeImport(): Observable<any> {
    // Suspend polling during resume operation to give it priority
    this.suspendPolling(3000);
    return this.serialRest.resumeImport();
  }
  
  abortImport(): Observable<any> {
    // Suspend polling during abort operation to give it priority
    this.suspendPolling(3000);
    return this.serialRest.abortImport();
  }
  
  isImportInProgress(): Observable<any> {
    return this.serialRest.isImportInProgress();
  }

  // Download Methods
  downloadImported(): void {
    this.serialRest.downloadImported().subscribe((blob: Blob) => {
      this.downloadFile(blob, 'imported.csv');
    });
  }

  downloadErrors(): void {
    this.serialRest.downloadErrors().subscribe((blob: Blob) => {
      this.downloadFile(blob, 'errors.csv');
    });
  }

  downloadRemaining(): void {
    this.serialRest.downloadRemaining().subscribe((blob: Blob) => {
      this.downloadFile(blob, 'remaining.csv');
    });
  }

  // Daytime Settings
  enableDaytimeStop(): Observable<any> {
    return this.serialRest.enableDaytimeStop();
  }

  disableDaytimeStop(): Observable<any> {
    return this.serialRest.disableDaytimeStop();
  }

  getDaytimeStatus(): Observable<any> {
    return this.serialRest.getDaytimeStatus();
  }

  getSSEUrl(): string {
    return this.serialRest.getSSEUrl();
  }

  private downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }
}
